/**
 * =============================================================================
 * SPEQ55 RFP Pro - Organization Management Admin Page
 * =============================================================================
 *
 * Admin page for creating and listing organizations.
 * - View all organizations with user counts
 * - Filter by active/inactive
 * - Create new organizations (auto-seeds a default parameter set)
 *
 * Access: super_admin, admin_sales (matches backend /organizations routes).
 *
 * Edit / deactivate / reactivate / delete are intentionally not wired up in
 * this page; the backend supports them but they are deferred to a follow-up
 * so this page can ship small and focused.
 *
 * @module pages/admin/OrganizationManagement
 * @author SPEQ55
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getOrganizations, createOrganization } from '../../api/organizations';

const NAME_MIN = 2;
const NAME_MAX = 255;
const DESCRIPTION_MAX = 2000;

function OrganizationManagement() {
  const { user } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');

  const canManageOrgs = user?.role === 'super_admin' || user?.role === 'admin_sales';

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = { includeStats: true, limit: 100 };
      if (!showInactive) {
        filters.is_active = true;
      }

      const data = await getOrganizations(filters);
      setOrganizations(data.organizations || []);
    } catch {
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    if (!canManageOrgs) return;
    loadOrganizations();
  }, [canManageOrgs, loadOrganizations]);

  const filteredOrganizations = organizations.filter((org) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      org.name?.toLowerCase().includes(search) || org.description?.toLowerCase().includes(search)
    );
  });

  const openCreateModal = () => {
    setCreateName('');
    setCreateDescription('');
    setModalError(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setModalError(null);
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setModalError(null);

    const trimmedName = createName.trim();
    const trimmedDescription = createDescription.trim();

    if (trimmedName.length < NAME_MIN || trimmedName.length > NAME_MAX) {
      setModalError(`Name must be between ${NAME_MIN} and ${NAME_MAX} characters.`);
      return;
    }

    if (trimmedDescription.length > DESCRIPTION_MAX) {
      setModalError(`Description must be at most ${DESCRIPTION_MAX} characters.`);
      return;
    }

    setModalLoading(true);

    try {
      const payload = { name: trimmedName };
      if (trimmedDescription) {
        payload.description = trimmedDescription;
      }

      const result = await createOrganization(payload);
      const createdName = result?.organization?.name || trimmedName;

      closeCreateModal();
      setSuccessMessage(
        `Organization "${createdName}" created. A "Standard RFP Analysis" parameter set was seeded.`
      );
      await loadOrganizations();
    } catch (err) {
      const responseData = err.response?.data;
      if (responseData?.errors?.length) {
        setModalError(responseData.errors.map((e) => e.message).join(' '));
      } else {
        setModalError(responseData?.error || 'Failed to create organization');
      }
    } finally {
      setModalLoading(false);
    }
  };

  if (!canManageOrgs) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organization Management</h1>
          <p className="text-gray-600 mt-1">Create and view organizations</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <span className="text-xl">+</span>
          New Organization
        </button>
      </div>

      {/* Success banner */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start justify-between">
          <p className="text-green-800">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-900 ml-4"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label htmlFor="org-search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="org-search"
              type="text"
              placeholder="Search by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show inactive
            </label>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Organizations table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-2 text-gray-600">Loading organizations...</p>
          </div>
        ) : filteredOrganizations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {organizations.length === 0
              ? 'No organizations yet. Click "+ New Organization" to create one.'
              : 'No organizations match your search.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrganizations.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{org.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm text-gray-600 max-w-md truncate"
                        title={org.description || ''}
                      >
                        {org.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {org.user_count ?? org.userCount ?? 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {org.is_active ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">New Organization</h3>

              {modalError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-sm text-red-800">{modalError}</p>
                </div>
              )}

              <form onSubmit={handleCreateOrganization}>
                <div className="mb-4">
                  <label
                    htmlFor="org-name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="org-name"
                    type="text"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    maxLength={NAME_MAX}
                    required
                    autoFocus
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Acme Corp"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {NAME_MIN}-{NAME_MAX} characters, must be unique.
                  </p>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="org-description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Description
                  </label>
                  <textarea
                    id="org-description"
                    value={createDescription}
                    onChange={(e) => setCreateDescription(e.target.value)}
                    maxLength={DESCRIPTION_MAX}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {createDescription.length} / {DESCRIPTION_MAX}
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeCreateModal}
                    disabled={modalLoading}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={modalLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {modalLoading ? 'Creating...' : 'Create Organization'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationManagement;
